import { Sprite } from "pixi.js";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Contract from "slotworks/utils/contracts/contract";
import Rectangle from "slotworks/utils/geom/rectangle";
import { getOffsetRectangle } from "slotworks/utils/position-offset-calculator";
import { Tween } from "slotworks/utils/tween";
import AnimateSymbolBehaviour from "./animate-symbol-behaviour";

export default class RemoveAnimateSymbolBehaviour extends AnimateSymbolBehaviour {
    public static PLAY_DEFAULT_ANIMATION = true;
    public static SCALE_UP_TO: number = 1.1;
    public static SCALE_UP_DURATION: number = 200;
    public static SCALE_DOWN_DURATION: number = 300;

    public behaviourName: string = "remove";

    private clonedSymbol?: Sprite;
    private originalScale: number;
    private scale: number;

    public remove(): Contract<void> {

        const animSuffixes: string[] = ["_remove", "_remove_animate"];
        if (this.hasAnimationFrames(animSuffixes)) {
            return new Contract<void>((resolve) => {
                return super.animate(["_remove", "_remove_animate"], [], true).then(() => {
                    this.symbol.setVisible(false);
                    resolve(null);
                });
            });
        } else if (RemoveAnimateSymbolBehaviour.PLAY_DEFAULT_ANIMATION) {
            return new Contract<void>((resolve) => {

                this.createSprite();

                this.symbol.setVisible(false);

                this.originalScale = this.clonedSymbol.scale.x;
                this.scale = this.originalScale;

                this.update();

                const scaleUpTween = new Tween(this)
                    .to({ scale: this.originalScale * RemoveAnimateSymbolBehaviour.SCALE_UP_TO }, RemoveAnimateSymbolBehaviour.SCALE_UP_DURATION)
                    .onUpdate(() => this.update());

                const scaleDownTween = new Tween(this)
                    .to({ scale: 0 }, RemoveAnimateSymbolBehaviour.SCALE_DOWN_DURATION)
                    .onUpdate(() => this.update());

                scaleUpTween.chain(scaleDownTween);

                scaleDownTween.onComplete(() => {
                    this.cleanup();
                    resolve();
                });

                scaleUpTween.start();
            });
        } else {
            this.symbol.setVisible(false);
            return Contract.empty();
        }
    }

    private createSprite() {
        this.cleanup();

        this.clonedSymbol = graphicsManager.createSprite(this.symbol.symbolId);
        this.animationLayer.add(this.clonedSymbol);

        const position: Rectangle = this.matrixLayer.getPosition(this.symbol.symbolId);
        const offset: Rectangle = getOffsetRectangle(this.matrixLayer, "symbol_1_1", `symbol_${this.symbol.gridPosition.x}_${this.symbol.gridPosition.y}`);

        this.clonedSymbol.width = position.width;
        this.clonedSymbol.height = position.height;
        this.clonedSymbol.x = position.x + offset.x + (position.width / 2);
        this.clonedSymbol.y = position.y + offset.y + (position.height / 2);
        this.clonedSymbol.anchor.set(0.5, 0.5);
    }

    private hasAnimationFrames(animSuffix: string[]): boolean {
        let verdict: boolean = false;
        const animNames: string[] = [];
        animSuffix.forEach((suffix: string) => animNames.push(`${this.symbol.symbolId}${suffix}`));
        if (graphicsManager.createAnimation(animNames) !== null) {
            verdict = true;
        }
        return verdict;
    }

    private update() {
        if (this.clonedSymbol) {
            this.clonedSymbol.scale.set(this.scale, this.scale);
        }
    }

    private cleanup() {
        this.symbol.setLayer(this.matrixLayer);
        if (this.clonedSymbol) {
            this.animationLayer.remove(this.clonedSymbol);
            this.clonedSymbol.destroy();
            this.clonedSymbol = undefined;
        }
    }
}
